import { hasYesOrNo, checkValue, checkValueIncluded } from 'models/validate';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import { DEFAULT_LATEST, THIS_MONTH } from 'constants/dateLimits';
import { LocationModel, ValidateJSModel } from 'forms/shared/types';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import { REQUIRED } from 'constants/errors';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { SF86 } from 'constants/formTypes';
import { formConfig } from 'config/forms';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { isSafeUnitedStates } from 'helpers/locations';
import { SubstanceAlcoholOrderedCounselingItemModel } from '../types';

const substanceAlcoholOrderedCounselingItemValidator: ValidateJSModel<SubstanceAlcoholOrderedCounselingItemModel> = {
  Seekers: (value, attributes, attributeName, options) => {
    if (options?.formType !== SF86) {
      return {};
    }
    const length = { minimum: 1, maximum: 6, tooShort: REQUIRED };

    if (value?.values?.includes('NotOrdered')) {
      length.maximum = 1;
    }

    return {
      array: {
        presence: { allowEmpty: false },
        length,
      },
    };
  },
  OtherSeeker: (value, attributes, attributeName, options) => {
    if (
      attributes?.Seekers
      && attributes?.Seekers.values
      && attributes?.Seekers.values.includes('Other')
      && options?.formType === SF86
    ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ActionTaken: { presence: true, hasValue: { validator: hasYesOrNo } },
  TreatmentProviderName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  TreatmentProviderAddress: (value, attributes, attributeName, options) => {
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      if (isPVQForm(options?.formType)) {
        if (attributes?.TreatmentProviderAddressNotApplicable?.applicable !== false) {
          return {
            presence: true,
            location: {
              validator: pvqLocationModelValidator,
              requireStreet: isSafeUnitedStates(value),
              requireIsMilitaryDiplomaticAddress: true,
              requireMilitaryDiplomaticInstallationName: isSafeUnitedStates(value),
              requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
            }
          };
        }

        return {};
      }

      return { presence: true, location: { validator: locationAddressModelValidator } };
    }
    return {};
  },
  TreatmentProviderAddressNotApplicable: {},
  TreatmentProviderTelephone: (value, attributes, attributeName, options) => {
    if (!isPVQForm(options?.formType) && checkValue(attributes?.ActionTaken, 'Yes')) {
      return { presence: true, model: { validator: phoneModelValidator, requireNumber: true } };
    }
    return {};
  },
  CounselingDates: (value, attributes, attributeName, options) => {
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      if (isPVQForm(options?.formType)) {
        return {
          presence: true,
          daterange: { earliest: setToStartOfMonth(options?.applicantBirthdate), latest: DEFAULT_LATEST },
        };
      }

      const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
      const years = formTypeConfig && formTypeConfig.SUBSTANCE_ALCOHOL_TREATMENT_YEARS;

      const beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
      let useDuration = false;

      if (years && beyondRequired?.valid && typeof years === 'number') {
        beyondRequired.datetime = beyondRequired.datetime.minus({ years });
        useDuration = !value?.present && isSafeDateLater(beyondRequired, setToStartOfMonth(getSafeDateTime(value?.to)));
      }

      return {
        presence: true,
        daterange: {
          requireYear: false,
          earliest: useDuration ? setToStartOfMonth(beyondRequired).datetime : setToStartOfMonth(options?.applicantBirthdate),
          tooEarly: useDuration ? 'error.beforeDuration' : 'error.applicantMin',
          latest: DEFAULT_LATEST
        },
      };
    }
    return {};
  },
  CompletedTreatment: (value, attributes) => {
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  NoCompletedTreatmentExplanation: (value, attributes) => {
    if (checkValue(attributes?.CompletedTreatment, 'No')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  NoActionTakenExplanation: (value, attributes) => {
    if (checkValue(attributes?.ActionTaken, 'No')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  OrderedByComments: {},
  Comments: {},
  CompletedTreatmentComments: {},
  NoActionTakenExplanationComments: {},
  TreatmentProviderNameNotApplicable: {},
  TreatmentProviderPersonName: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType) && checkValue(attributes?.ActionTaken, 'Yes') && attributes?.TreatmentProviderNameNotApplicable?.applicable !== false) {
      return {
        presence: true,
        model: {
          validator: pvqNameModelValidator,
          requireMiddleName: false
        }
      };
    }
    return {};
  },
  TreatmentProviderPhones: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ActionTaken?.value === 'Yes') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: {
                validator: phoneModelValidator,
                requireNumberType: true
              },
            },
          },
        },
      };
    }

    return {};
  },
  TreatmentProviderEmails: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ActionTaken?.value === 'Yes') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
                requireEmailType: false,
              },
            },
          },
        },
      };
    }
    return {};
  },
  OrderedBy: (value, attributes, attributeName, options) => {
    if (!isPVQForm(options?.formType)) { return {}; }

    return { presence: true, hasValue: true };
  },
  OrderedByExplanation: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType) && attributes?.OrderedBy?.value === 'Other') {
      return { presence: true, hasValue: true };
    }

    return {};
  },
};

export default substanceAlcoholOrderedCounselingItemValidator;
